import { DataGrid } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import Button from "../common/Button";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import api from "../../api";

export default function PendingReportList() {
  const [reports, setReports] = useState([]);
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [selectedRows, setSelectedRows] = useState([]); // State to store selected reports

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const { data } = await api.get(
        `${process.env.REACT_APP_API_BASE_URL}report/pending`
      );
      setReports(data);
    } catch (error) {
      console.error("Error fetching reports:", error);
    }
  };

  const handleApprove = async (reportId) => {
    try {
      await api.put(
        `${process.env.REACT_APP_API_BASE_URL}report/approve/${reportId}`
      );
      fetchReports();
    } catch (error) {
      console.error("Error approving report:", error);
    }
  };

  const handleApproveMultiple = async () => {
    try {
      await Promise.all(
        selectedRows.map((id) =>
          api.put(`${process.env.REACT_APP_API_BASE_URL}report/approve/${id}`)
        )
      );
      fetchReports();
      setSelectedRows([]); // Clear the selected rows after approval
    } catch (error) {
      console.error("Error approving selected reports:", error);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert("Please enter a rejection reason.");
      return;
    }
    try {
      await api.put(
        `${process.env.REACT_APP_API_BASE_URL}report/reject/${selectedReportId}`,
        { rejectedReason: rejectionReason }
      );
      setOpenRejectModal(false);
      setRejectionReason("");
      fetchReports();
    } catch (error) {
      console.error("Error rejecting report:", error);
    }
  };

  const handleRejectMultiple = async () => {
    if (!rejectionReason.trim()) {
      alert("Please enter a rejection reason.");
      return;
    }
    try {
      await Promise.all(
        selectedRows.map((id) =>
          api.put(
            `${process.env.REACT_APP_API_BASE_URL}report/reject/${id}`,
            { rejectedReason: rejectionReason }
          )
        )
      );
      setOpenRejectModal(false);
      setRejectionReason("");
      fetchReports();
      setSelectedRows([]); // Clear the selected rows after rejection
    } catch (error) {
      console.error("Error rejecting selected reports:", error);
    }
  };

  const openRejectPopup = (reportId) => {
    setSelectedReportId(reportId);
    setOpenRejectModal(true);
  };

  const closeRejectPopup = () => {
    setOpenRejectModal(false);
    setRejectionReason("");
  };

  const handleSelectRow = (id) => {
    setSelectedRows((prevSelectedRows) => {
      if (prevSelectedRows.includes(id)) {
        return prevSelectedRows.filter((rowId) => rowId !== id);
      } else {
        return [...prevSelectedRows, id];
      }
    });
  };

  const columns = [
    {
      field: "checkbox",
      headerName: "Select",
      width: 100,
      renderCell: (params) => (
        <input
          type="checkbox"
          checked={selectedRows.includes(params.row._id)}
          onChange={() => handleSelectRow(params.row._id)}
        />
      ),
    },
    { field: "name", headerName: "Name", flex: 1, minWidth: 150, headerClassName: "custom-header" },
    { field: "topic", headerName: "Topic", flex: 2, minWidth: 200, headerClassName: "custom-header" },
    { field: "description", headerName: "Description", flex: 3, minWidth: 200, headerClassName: "custom-header" },
    {
      field: "actions",
      headerName: "Actions",
      flex: 2,
      minWidth: 200,
      headerClassName: "custom-header",
      renderCell: (params) => (
        <>
          <Button onClick={() => handleApprove(params.row._id)}>Approve</Button>
          <Button onClick={() => openRejectPopup(params.row._id)} className="ml-2 bg-red-500">
            Reject
          </Button>
        </>
      ),
    },
  ];

  return (
    <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-4 shadow-lg border border-white/10">
      <div className="mb-4">
        <Button onClick={handleApproveMultiple} disabled={selectedRows.length === 0}>
          Approve Selected
        </Button>
        <Button onClick={handleRejectMultiple} disabled={selectedRows.length === 0} className="ml-4 bg-red-500">
          Reject Selected
        </Button>
      </div>
      <DataGrid
        rows={reports || []}
        columns={columns}
        getRowId={(row) => row._id}
        initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
        pageSizeOptions={[5, 10]}
        sx={{
          // Glassy root container
    backgroundColor: "rgba(255, 255, 255, 0.03)",
    border: "none",
    borderRadius: 3,
    backdropFilter: "blur(8px)",
  
    // Top header row
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: "rgba(255, 255, 255, 0.08)",
      backdropFilter: "blur(12px)",
      borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
    },
  
    // Footer container (bottom row)
    "& .MuiDataGrid-footerContainer": {
      backgroundColor: "rgba(255,255,255,0.05)",
      borderTop: "1px solid rgba(255,255,255,0.1)",
      backdropFilter: "blur(10px)",
    },
  
    // Ensure rows have no override from CSS variable
    "& .MuiDataGrid-row": {
      backgroundColor: "transparent !important",
    },
  
    // Optional: override containerBackground if needed
    "--DataGrid-containerBackground": "transparent",
        }}
      />

      {/* Reject Modal */}
      <Modal open={openRejectModal} onClose={closeRejectPopup}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            p: 4,
            borderRadius: 2,
            boxShadow: 24,
          }}
        >
          <h2>Reject Report</h2>
          <TextField
            label="Reason for Rejection"
            multiline
            fullWidth
            rows={3}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            sx={{ mt: 2 }}
          />
          <div className="flex justify-end mt-4">
            <Button onClick={closeRejectPopup} className="mr-2">
              Cancel
            </Button>
            <Button onClick={handleReject} className="bg-red-500">
              Submit
            </Button>
          </div>
        </Box>
      </Modal>
    </div>
  );
}
